import { Component } from "@theme/component";
import { CartUpdateEvent, ThemeEvents } from "@theme/events";

/**
 * A custom element that manages the navigation button functionality.
 *
 * @extends {Component}
 */
class NavigationButton extends Component {
  connectedCallback() {
    super.connectedCallback();

    const trigger = this.querySelector(".navigation-button__trigger");
    if (trigger) {
      trigger.addEventListener("click", this.#toggleDropdown);
    }

    this.addEventListener("keydown", this.#handleKeydown);

    // Close dropdown when clicking outside
    document.addEventListener("click", this.#handleOutsideClick);

    // Close dropdown on escape key
    document.addEventListener("keydown", this.#handleEscapeKey);

    // Listen for cart update events to show animation feedback
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("click", this.#handleOutsideClick);
    document.removeEventListener("keydown", this.#handleEscapeKey);
    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate
    );
  }

  /**
   * Toggle the dropdown menu
   */
  #toggleDropdown = () => {
    const dropdown = this.querySelector(".navigation-dropdown");
    const isExpanded = this.getAttribute("aria-expanded") === "true";

    if (isExpanded) {
      this.#closeDropdown();
    } else {
      this.#openDropdown();
    }
  };

  /**
   * Open the dropdown menu
   */
  #openDropdown = () => {
    const dropdown = this.querySelector(".navigation-dropdown");

    if (!dropdown) return;

    const header = document.querySelector(".header__columns");
    if (header && header instanceof HTMLElement) {
      const headerWidth = header.offsetWidth;
      this.style.width = `${headerWidth < 465 ? headerWidth : 465}px`;
    } else {
      this.style.width = "465px";
    }

    this.setAttribute("aria-expanded", "true");
    dropdown.setAttribute("aria-hidden", "false");
  };

  /**
   * Close the dropdown menu
   */
  #closeDropdown = () => {
    const dropdown = this.querySelector(".navigation-dropdown");

    if (!dropdown) return;

    this.setAttribute("aria-expanded", "false");
    dropdown.setAttribute("aria-hidden", "true");

    // Return focus to button
    if (this instanceof HTMLElement) {
      this.focus();
    }
  };

  /**
   * Handle keyboard navigation
   */
  #handleKeydown = (/** @type {KeyboardEvent} */ event) => {
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        this.#toggleDropdown();
        break;
    }
  };

  /**
   * Handle escape key
   */
  #handleEscapeKey = (/** @type {KeyboardEvent} */ event) => {
    if (event.key === "Escape") {
      this.#closeDropdown();
    }
  };

  /**
   * Handle clicks outside the menu
   */
  #handleOutsideClick = (/** @type {MouseEvent} */ event) => {
    if (event.target instanceof Element && !this.contains(event.target)) {
      this.#closeDropdown();
    }
  };

  /**
   * Handle cart update events to show animation feedback
   * @param {CartUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;

    // Find the featured product image wrapper and add animation class
    const imageWrapper = this.querySelector(
      ".navigation-featured-product__image-wrapper"
    );
    if (imageWrapper) {
      imageWrapper.classList.add("adding-to-cart");

      // Remove the animation class after animation completes
      setTimeout(() => {
        imageWrapper.classList.remove("adding-to-cart");
      }, 600);
    }
  };
}

if (!customElements.get("navigation-button")) {
  customElements.define("navigation-button", NavigationButton);
}
